/**
 * Documents-and-assets intents (agent parity phase 2): image and raster
 * layers from bytes, image replacement, duplication and pasting layers from
 * other documents.
 *
 * Each of them prepares outside history, through the same code the UI uses
 * (`stageResourceLayer` behind Place image, `stageReplaceImageSource` behind
 * Replace image, `stagePasteLayerClipboard` behind ⌘J and dropping a layer
 * on another tab), and commits synchronously inside the transaction. The
 * heavy modules are imported on first use, so the UI's synchronous path
 * does not load them.
 *
 * A prepared resource change is bound to the session revision and resource
 * table it was staged against: if either moved before the commit, the
 * intent is refused with CONFLICT and the agent retries. Because each stage
 * extends the document's resource table on its own, a transaction carries at
 * most one intent that adds images or fonts; a second one is refused with
 * LIMIT_EXCEEDED before anything changes.
 */
import type { AssetInput, ImageReplaceIntent, LayerCreateIntent, LayerDuplicateIntent, LayerId, LayerPasteIntent } from '@maipo/agent-protocol';
import { AddLayerCommand, type Command } from '../../core/commands';
import { createLayerBase, createRasterLayer, findLayer, findLayerParent, layerRetainedBytes, validateDimensions, type EditorDocument, type ImageLayer, type Layer, type RasterLayer } from '../../core/model';
import type { DocumentSession } from '../../core/session';
import { RasterTileStore } from '../../core/tiles';
import { ReplaceRasterCommand } from '../../core/template';
import type { StagedLayerPaste } from '../../public-format/layer-clipboard';
import type { StagedImageReplacement } from '../../public-format/replace-image-source';
import type { StagedResourceLayer } from '../../public-format/transfer-layer';
import { rasterImageDecoder, resolveAsset, resolvedBlob, type ResolvedAsset } from '../assets';
import { defineIntent, IntentRejection, type Diagnostic, type PrepareContext } from '../registry';
import { checkContainerUnlocked, checkUnlocked, error, isAlias, layerFor, parentKey, placementReads, requireLayer, resolvePlacement, resolveRef, staticIds, validatePlacement } from './common';

/** What a resource-staging intent hands from prepare to compile. */
interface StagedResource {
  session: DocumentSession;
  /** The session revision when staging finished. */
  revision: number;
  staged: Pick<StagedResourceLayer | StagedLayerPaste | StagedImageReplacement, 'current' | 'release'> & { layer?: Layer };
  ids?: Readonly<Record<string, string>>;
  source?: LayerId;
}

/** Prepare runs before any intent of the transaction executes, so it can only address layers that exist now. */
function existingLayer(session: DocumentSession, ref: string, what: string): Layer {
  if (isAlias(ref)) throw new IntentRejection('UNSUPPORTED_COMMAND', `${what} prepares before the transaction runs, so it needs an existing layer id, not an alias of a layer created in the same transaction.`);
  const layer = findLayer(session.document.root, ref);
  if (!layer) throw new IntentRejection('LAYER_NOT_FOUND', `Layer ${JSON.stringify(ref).slice(0, 80)} does not exist.`);
  return layer;
}

function stagedOrReject<T>(outcome: T | { rejected: string }): T {
  if (outcome && typeof outcome === 'object' && 'rejected' in outcome) throw new IntentRejection('CONFLICT', `${outcome.rejected}`);
  return outcome;
}

/**
 * The staged change still fits the document: same session revision, same
 * resource table. Within one transaction the table moves only through an
 * earlier resource-staging intent, which is refused as a limit.
 */
function requireCurrent(prepared: StagedResource, document: EditorDocument, intentLabel: string): void {
  if (prepared.staged.current(prepared.session, document)) return;
  // Same revision and no gesture: only an earlier intent of this transaction can have moved the resource table.
  if (prepared.session.currentRevision === prepared.revision && !prepared.session.history.hasLiveCommand) {
    throw new IntentRejection('LIMIT_EXCEEDED', `${intentLabel}: a transaction can add or replace images (or copy layers with images or fonts) in one intent only; split it into separate transactions.`);
  }
  throw new IntentRejection('CONFLICT', `${intentLabel}: the document changed while its resources were prepared; inspect again and retry.`);
}

async function resolve(input: AssetInput, session: DocumentSession, signal: AbortSignal, context: PrepareContext): Promise<ResolvedAsset> {
  return resolveAsset(input, { session, signal, assets: context.assets, decode: context.decode });
}

/** Charges resampled output pixels (the natural decode was charged when the asset resolved). */
function chargeOutput(context: PrepareContext, size: { width: number; height: number }, what: string): void {
  context.decode?.charge(size.width * size.height * 4, `${what}: ${size.width} × ${size.height} pixels`);
}

/** Decoded pixel sizes obey the editor's canvas limits (32,768 px a side, 1.5 GiB decoded). */
function checkPixelSize(size: { width: number; height: number }, what: string): void {
  try { validateDimensions(size.width, size.height); } catch (cause) {
    throw new IntentRejection('LIMIT_EXCEEDED', `${what}: ${cause instanceof Error ? cause.message : String(cause)}`);
  }
}

async function sourceAsset(resolved: ResolvedAsset, signal: AbortSignal) {
  if (resolved.kind === 'existing') return resolved.asset;
  const { readImageSourceAsset } = await import('../../public-format/image-source-asset');
  return readImageSourceAsset(resolved.blob, signal);
}

/** Output size for decoded pixels: its own, exact, or one side with the aspect ratio kept. */
export function scaledSize(natural: { width: number; height: number }, width?: number, height?: number): { width: number; height: number } {
  if (width !== undefined && height !== undefined) return { width, height };
  if (width !== undefined) return { width, height: Math.max(1, Math.round((width * natural.height) / natural.width)) };
  if (height !== undefined) return { width: Math.max(1, Math.round((height * natural.width) / natural.height)), height };
  return { ...natural };
}

// ---------------------------------------------------------------------------
// layer.create { kind: 'image' } and { kind: 'raster', asset }

type ImageSpec = Extract<LayerCreateIntent['spec'], { kind: 'image' }>;
type RasterSpec = Extract<LayerCreateIntent['spec'], { kind: 'raster' }>;

/** Prepared `layer.create` for kinds built from bytes. */
export type PreparedCreate = { kind: 'resource'; resource: StagedResource & { staged: StagedResourceLayer } } | { kind: 'raster'; layer: RasterLayer };

export async function prepareImageLayer(session: DocumentSession, spec: ImageSpec, signal: AbortSignal, context: PrepareContext): Promise<PreparedCreate> {
  const resolved = await resolve(spec.asset, session, signal, context);
  const asset = await sourceAsset(resolved, signal);
  const layer: ImageLayer = { ...createLayerBase(spec.name), kind: 'image', image: { asset: asset.id, frame: { width: 1, height: 1, fit: 'contain' } } };
  const { stageResourceLayer } = await import('../../public-format/transfer-layer');
  const staged = stagedOrReject(await stageResourceLayer({ fonts: [], assets: [asset] }, session, layer, {
    signal,
    label: 'Place image',
    // Its own pixel size, `width` and/or `height` (one of them keeps the aspect ratio), at `at`.
    // `fit` (default contain, as Place image) is how the image fills that frame.
    place: (image) => ({ frame: { ...scaledSize(image, spec.width, spec.height), fit: spec.fit ?? 'contain' }, transform: [1, 0, 0, 1, spec.at.x, spec.at.y] }),
  }));
  return { kind: 'resource', resource: { session, revision: session.currentRevision, staged } };
}

function fillStore(store: RasterTileStore, color: string, alpha = 1): void {
  const value = Number.parseInt(color.slice(1), 16);
  const rgba = [(value >> 16) & 255, (value >> 8) & 255, value & 255, Math.round(Math.max(0, Math.min(1, alpha)) * 255)];
  if (rgba[3] === 0) return;
  const columns = Math.ceil(store.width / store.tileSize);
  const rows = Math.ceil(store.height / store.tileSize);
  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < columns; x += 1) {
      const dimensions = store.tileDimensions(x, y);
      const pixels = new Uint8ClampedArray(dimensions.width * dimensions.height * 4);
      for (let offset = 0; offset < pixels.length; offset += 4) pixels.set(rgba, offset);
      store.setTile(x, y, pixels);
    }
  }
}

/** A raster layer of `width` × `height` (the layer panel's "New raster layer" is the document size, unfilled). */
export function buildRasterLayer(spec: RasterSpec): RasterLayer {
  if (spec.width === undefined || spec.height === undefined) throw new IntentRejection('SCHEMA_INVALID', 'A raster layer without an asset needs width and height.');
  const layer = createRasterLayer(spec.name, spec.width, spec.height);
  if (spec.fill) fillStore(layer.raster, spec.fill.color.toLowerCase(), spec.fill.alpha);
  if (spec.at) layer.transform = [1, 0, 0, 1, spec.at.x, spec.at.y];
  return layer;
}

export async function prepareRasterFromAsset(session: DocumentSession, spec: RasterSpec, signal: AbortSignal, context: PrepareContext): Promise<PreparedCreate> {
  if (!spec.asset) throw new IntentRejection('SCHEMA_INVALID', 'No asset to decode.');
  const resolved = await resolve(spec.asset, session, signal, context);
  const blob = resolvedBlob(resolved);
  const decoder = rasterImageDecoder();
  // The header's size when preflight read it (no extra decode); the decoder's otherwise.
  const natural = resolved.size.width > 0 && resolved.size.height > 0 ? resolved.size : await decoder.measure(blob, signal);
  const size = scaledSize(natural, spec.width, spec.height);
  checkPixelSize(size, 'layer.create');
  // Both sides given: `cover` (default) crops centred, `exact` stretches; one side keeps the aspect ratio.
  const resample = spec.width === undefined && spec.height === undefined ? undefined : { ...size, fit: spec.width !== undefined && spec.height !== undefined ? spec.fit ?? 'cover' : 'exact' as const };
  if (resample) chargeOutput(context, size, 'layer.create');
  const image = await decoder.decode(blob, resample, signal);
  const layer = createRasterLayer(spec.name, image.width, image.height);
  layer.raster = RasterTileStore.fromImageData(image);
  if (spec.at) layer.transform = [1, 0, 0, 1, spec.at.x, spec.at.y];
  return { kind: 'raster', layer };
}

export function compileCreatedFromBytes(document: EditorDocument, intent: LayerCreateIntent, prepared: PreparedCreate | undefined, aliases: Map<string, LayerId>): Command[] {
  if (!prepared) throw new IntentRejection('UNSUPPORTED_COMMAND', `Creating ${intent.spec.kind} layers from bytes prepares asynchronously; it is not available on the synchronous path.`);
  const target = resolvePlacement(document, intent.to, aliases);
  if (prepared.kind === 'raster') {
    if (intent.as) aliases.set(intent.as, prepared.layer.id);
    return [new AddLayerCommand(prepared.layer, target.parentId, target.index)];
  }
  const resource = prepared.resource as StagedResource & { staged: StagedResourceLayer };
  requireCurrent(resource, document, 'layer.create');
  if (intent.as) aliases.set(intent.as, resource.staged.layer.id);
  return [resource.staged.command(document, target.parentId, target.index)];
}

export function releasePreparedCreate(prepared: unknown): void {
  const value = prepared as PreparedCreate | undefined;
  if (value?.kind === 'resource') value.resource.staged.release();
}

// ---------------------------------------------------------------------------
// layer.duplicate

interface PreparedPaste extends StagedResource { staged: StagedLayerPaste; ids: Readonly<Record<string, string>>; source: LayerId }

export const layerDuplicate = defineIntent<LayerDuplicateIntent>({
  type: 'layer.duplicate',
  version: 1,
  footprint: (document, intent, aliases) => {
    const ids = staticIds([intent.layer], aliases);
    const id = ids[0];
    return { reads: id && findLayer(document.root, id) ? [id, parentKey(document, id)] : ids, writes: [], structural: true, document: false };
  },
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (!layer) return diagnostics;
    const location = findLayerParent(document.root, layer.id);
    if (location) checkContainerUnlocked(document, location.parent, diagnostics, layer.id, context.policy);
    return diagnostics;
  },
  prepare: async (session, intent, signal, context) => {
    const layer = existingLayer(session, intent.layer, 'layer.duplicate');
    // A copy holds as many pixel bytes as its source.
    context.decode?.charge(layerRetainedBytes(layer), `Duplicating layer ${layer.id}`);
    const { duplicateClipboard, stagePasteLayerClipboard } = await import('../../public-format/layer-clipboard');
    let clipboard;
    try { clipboard = duplicateClipboard(session.document, layer); } catch (cause) {
      throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} cannot be duplicated: ${cause instanceof Error ? cause.message : String(cause)}`, layer.id);
    }
    const staged = stagedOrReject(await stagePasteLayerClipboard(clipboard, session, { signal, label: `Duplicate ${layer.name}`, recordDuplicate: true }));
    return { session, revision: session.currentRevision, staged, ids: staged.ids, source: layer.id } satisfies PreparedPaste;
  },
  // Aliases of earlier intents name layers that exist only inside this transaction; their copy
  // would need a second staging pass, so `layer` must be an existing id.
  needsPrepare: () => true,
  releasePrepared: (_intent, prepared) => (prepared as PreparedPaste | undefined)?.staged.release(),
  copies: (_intent, prepared) => {
    const value = prepared as PreparedPaste | undefined;
    return value ? { source: value.source, layer: value.staged.layer.id, ids: { ...value.ids } } : undefined;
  },
  compile: (document, intent, prepared, aliases) => {
    const value = prepared as PreparedPaste | undefined;
    if (!value) throw new IntentRejection('UNSUPPORTED_COMMAND', 'layer.duplicate prepares asynchronously.');
    requireLayer(document, value.source, aliases);
    requireCurrent(value, document, 'layer.duplicate');
    const location = findLayerParent(document.root, value.source);
    const parentId = !location || location.parent === document.root ? 'root' : location.parent.id;
    if (intent.as) aliases.set(intent.as, value.staged.layer.id);
    return [value.staged.command(document, parentId, location?.index ?? 0)];
  },
  describe: (_document, intent, aliases) => [{ layer: intent.as ?? '(new layer)', path: '(created)', before: null, after: { duplicateOf: resolveRef(intent.layer, aliases) ?? intent.layer } }],
});

// ---------------------------------------------------------------------------
// layer.paste (a layer captured from another open document by maipo_copy_layer)

export const layerPaste = defineIntent<LayerPasteIntent>({
  type: 'layer.paste',
  version: 1,
  footprint: (document, intent, aliases) => ({ reads: placementReads(document, intent.to, aliases), writes: [], structural: true, document: false }),
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    validatePlacement(document, intent.to, context, diagnostics);
    return diagnostics;
  },
  prepare: async (session, intent, signal, context) => {
    const clipboard = context.clipboards?.take(intent.clipboard);
    if (!clipboard) throw new IntentRejection('COMMAND_REJECTED', `Clipboard ${intent.clipboard} is not available; maipo_copy_layer issues clipboard handles for one call.`);
    context.decode?.charge(layerRetainedBytes(clipboard.layer), 'Pasting a layer');
    const { stagePasteLayerClipboard } = await import('../../public-format/layer-clipboard');
    const staged = stagedOrReject(await stagePasteLayerClipboard(clipboard, session, { signal }));
    return { session, revision: session.currentRevision, staged, ids: staged.ids, source: clipboard.layer.id } satisfies PreparedPaste;
  },
  releasePrepared: (_intent, prepared) => (prepared as PreparedPaste | undefined)?.staged.release(),
  copies: (_intent, prepared) => {
    const value = prepared as PreparedPaste | undefined;
    return value ? { source: value.source, layer: value.staged.layer.id, ids: { ...value.ids } } : undefined;
  },
  compile: (document, intent, prepared, aliases) => {
    const value = prepared as PreparedPaste | undefined;
    if (!value) throw new IntentRejection('UNSUPPORTED_COMMAND', 'layer.paste prepares asynchronously.');
    requireCurrent(value, document, 'layer.paste');
    const target = resolvePlacement(document, intent.to, aliases);
    if (intent.as) aliases.set(intent.as, value.staged.layer.id);
    return [value.staged.command(document, target.parentId, target.index)];
  },
  describe: (_document, intent) => [{ layer: intent.as ?? '(new layer)', path: '(created)', before: null, after: { pastedFrom: intent.clipboard } }],
});

// ---------------------------------------------------------------------------
// image.replace

type PreparedReplace =
  | { kind: 'image'; resource: StagedResource & { staged: StagedImageReplacement } }
  | { kind: 'image-unchanged' }
  | { kind: 'raster'; layerId: LayerId; store: RasterTileStore; width: number; height: number };

export const imageReplace = defineIntent<ImageReplaceIntent>({
  type: 'image.replace',
  version: 1,
  footprint: (_document, intent, aliases) => {
    const ids = staticIds([intent.layer], aliases);
    return { reads: ids, writes: ids, structural: false, document: false };
  },
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (layer.kind !== 'image' && layer.kind !== 'raster') { diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer; image.replace takes image or raster layers.`, layer.id)); return diagnostics; }
    checkUnlocked(document, layer, 'pixels', diagnostics, context.policy);
    if (layer.kind === 'image' && intent.fit) diagnostics.push({ severity: 'warning', code: 'fit-ignored', message: 'Image layers keep their frame and its fit; `fit` applies to raster layers.', layer: layer.id });
    return diagnostics;
  },
  prepare: async (session, intent, signal, context): Promise<PreparedReplace> => {
    const layer = existingLayer(session, intent.layer, 'image.replace');
    const resolved = await resolve(intent.asset, session, signal, context);
    if (layer.kind === 'image') {
      const { stageReplaceImageSource } = await import('../../public-format/replace-image-source');
      const outcome = await stageReplaceImageSource(session, layer.id, resolved.kind === 'existing' ? resolved.asset : resolved.blob, signal);
      if ('noOp' in outcome) return { kind: 'image-unchanged' };
      return { kind: 'image', resource: { session, revision: session.currentRevision, staged: stagedOrReject(outcome) } };
    }
    if (layer.kind !== 'raster') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer.`, layer.id);
    const { width, height } = layer.raster;
    chargeOutput(context, { width, height }, 'image.replace');
    const image = await rasterImageDecoder().decode(resolvedBlob(resolved), { width, height, fit: intent.fit ?? 'cover' }, signal);
    return { kind: 'raster', layerId: layer.id, store: RasterTileStore.fromImageData(image), width, height };
  },
  releasePrepared: (_intent, prepared) => {
    const value = prepared as PreparedReplace | undefined;
    if (value?.kind === 'image') value.resource.staged.release();
  },
  compile: (document, intent, prepared, aliases) => {
    const value = prepared as PreparedReplace | undefined;
    if (!value) throw new IntentRejection('UNSUPPORTED_COMMAND', 'image.replace prepares asynchronously.');
    const layer = requireLayer(document, intent.layer, aliases);
    if (value.kind === 'image-unchanged') return [];
    if (value.kind === 'image') {
      requireCurrent(value.resource, document, 'image.replace');
      return [value.resource.staged.command(document)];
    }
    if (layer.kind !== 'raster' || layer.raster.width !== value.width || layer.raster.height !== value.height) throw new IntentRejection('CONFLICT', `Raster layer ${layer.id} changed size while the image was prepared; retry.`, layer.id);
    return [new ReplaceRasterCommand(layer.id, value.store, 'Replace image')];
  },
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases);
    const layer = id ? findLayer(document.root, id) : undefined;
    return [{ layer: id ?? intent.layer, path: layer?.kind === 'image' ? 'image.asset' : 'raster.pixels', before: layer?.kind === 'image' ? layer.image.asset : null, after: 'replaced' }];
  },
});

